import { Fragment, memo, useState, type FormEvent, type ReactNode } from 'react'
import { Search, Loader2 } from 'lucide-react'
import { Button } from '@highlight/ui'
import { getChatApiUrl } from '@renderer/chat/api/v1Client'
import {
  type EntityIndexEntry,
  type HybridSearchMode,
  useHybridSearch,
  HybridSearchResult,
  SourceIngestState,
  SourceWatermark,
} from '../hooks/use-hybrid-search'
import { Artifact } from './artifact'

const emptyReasonMessages: Record<string, string> = {
  'no-ingestion-namespace': 'No ingestion namespace yet — context ingestion has not run for this account.',
  'namespace-not-found': 'Ingestion namespace exists in Postgres but not in Turbopuffer yet.',
  'target-unavailable': 'No indexed content matches that source/modality filter combination.',
}

const ingestStateLabel: Record<SourceIngestState, string> = {
  fresh: 'fresh',
  backfilling: 'backfilling',
  disconnected: 'disconnected',
  terminal_failure: 'failed',
}

const ingestStateColor: Record<SourceIngestState, string> = {
  fresh: 'bg-green',
  backfilling: 'bg-yellow',
  disconnected: 'bg-red',
  terminal_failure: 'bg-red',
}

// The server returns UTC instants; render them in the viewer's own locale and timezone, and name the
// zone so the displayed time is unambiguous.
const formatEventTime = (iso: string): string => new Date(iso).toLocaleString(undefined, { timeZoneName: 'short' })

const filterInputClassName =
  'bg-element text-foreground placeholder:text-foreground-placeholder h-8 flex-1 rounded-lg border px-3 text-xs outline-none'

type SubmittedSearch = {
  query: string
  mode: HybridSearchMode
  source: string
  modality: string
}

export const SearchContent = memo(() => {
  const [input, setInput] = useState('')
  const [source, setSource] = useState('')
  const [modality, setModality] = useState('')
  const [mode, setMode] = useState<HybridSearchMode>('fts')
  const [submitted, setSubmitted] = useState<SubmittedSearch | null>(null)
  const [groupByEntity, setGroupByEntity] = useState(false)
  const { data, isFetching, error, refetch } = useHybridSearch({
    query: submitted?.query ?? '',
    mode: submitted?.mode ?? 'fts',
    source: submitted?.source,
    modality: submitted?.modality,
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    // Re-running an identical search must still hit the backend — checking how
    // fresh ingestion changed the results is the whole point of this tab.
    if (
      submitted &&
      submitted.query === input &&
      submitted.mode === mode &&
      submitted.source === source &&
      submitted.modality === modality
    ) {
      void refetch()
      return
    }
    setSubmitted({ query: input, mode, source, modality })
  }

  return (
    <div className="flex w-full flex-col gap-6 overflow-y-auto p-8">
      <div className="flex items-center gap-3">
        <div className="bg-element flex h-10 w-10 items-center justify-center rounded-lg">
          <Search size={20} className="text-foreground-secondary" />
        </div>
        <div className="flex flex-col">
          <h2 className="text-foreground text-lg font-medium">Search</h2>
          <p className="text-foreground-secondary text-sm">
            Full-text ordering with semantic backfill; semantic-only mode is available for debugging
          </p>
          <p className="text-foreground-placeholder text-xs" data-testid="hybrid-search-backend">
            {`backend: ${getChatApiUrl()}`}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-2" data-testid="hybrid-search-form">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Search your context…"
            data-testid="hybrid-search-input"
            className="bg-element text-foreground placeholder:text-foreground-placeholder h-10 flex-1 rounded-lg border px-3 text-sm outline-none"
          />
          <Button
            variant="secondary"
            type="submit"
            size="sm"
            disabled={isFetching || input.trim().length === 0}
            className="gap-2"
          >
            {isFetching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {isFetching ? 'Searching…' : 'Search'}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            placeholder="Filter by source (optional)"
            data-testid="hybrid-search-source"
            className={filterInputClassName}
          />
          <input
            type="text"
            value={modality}
            onChange={(event) => setModality(event.target.value)}
            placeholder="Filter by modality (optional)"
            data-testid="hybrid-search-modality"
            className={filterInputClassName}
          />
        </div>
        <label className="text-foreground-secondary flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={mode === 'semantic'}
            onChange={(event) => setMode(event.target.checked ? 'semantic' : 'fts')}
            data-testid="hybrid-search-semantic-toggle"
          />
          semantic only
        </label>
      </form>

      {error && (
        <div className="bg-red/10 text-red rounded-lg p-4 text-sm" data-testid="hybrid-search-error">
          {error.message || `Search failed with no message (backend: ${getChatApiUrl()})`}
        </div>
      )}

      {data && data.reason && (
        <p className="text-foreground-secondary text-sm" data-testid="hybrid-search-empty-reason">
          {emptyReasonMessages[data.reason] ?? data.reason}
        </p>
      )}

      {data && !data.reason && data.results.length === 0 && (
        <p className="text-foreground-secondary text-sm" data-testid="hybrid-search-no-results">
          No results for “{submitted?.query}”.
        </p>
      )}

      {data && data.sourceWatermarks && data.sourceWatermarks.length > 0 && (
        <SourceWatermarksStrip watermarks={data.sourceWatermarks} />
      )}

      {data && data.results.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="hybrid-search-results">
          <label className="text-foreground-secondary flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={groupByEntity}
              onChange={(event) => setGroupByEntity(event.target.checked)}
              data-testid="hybrid-search-group-toggle"
            />
            group by entity
          </label>
          {groupByEntity && data.entities ? (
            <GroupedResults results={data.results} entities={data.entities} />
          ) : (
            data.results.map((result) => <SearchResultRow key={result.id} result={result} />)
          )}
        </div>
      )}
    </div>
  )
})

SearchContent.displayName = 'SearchContent'

const SourceWatermarksStrip = memo(({ watermarks }: { watermarks: Array<SourceWatermark> }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="hybrid-search-watermarks">
    {watermarks.map((watermark) => (
      <span
        key={watermark.source}
        className="bg-element text-foreground-secondary flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs"
      >
        <span className={`${ingestStateColor[watermark.ingestState]} h-1.5 w-1.5 rounded-full`} />
        <span className="font-medium">{watermark.source}</span>
        <span className="text-foreground-placeholder">
          {watermark.ingestState === 'fresh' && watermark.freshUpTo
            ? `fresh to ${new Date(watermark.freshUpTo).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' })}`
            : ingestStateLabel[watermark.ingestState]}
        </span>
      </span>
    ))}
  </div>
))

SourceWatermarksStrip.displayName = 'SourceWatermarksStrip'

/** Tokens per result position, derived from the gateway's entity index — the collapse's one owner. */
function tokensByResult(entities: ReadonlyArray<EntityIndexEntry>) {
  const tokens = new Map<number, string[]>()
  for (const entity of entities) {
    for (const index of entity.resultIndices) {
      const list = tokens.get(index)
      if (list) list.push(entity.token)
      else tokens.set(index, [entity.token])
    }
  }
  return tokens
}

const GroupedResults = memo(
  ({
    results,
    entities,
  }: {
    results: ReadonlyArray<HybridSearchResult>
    entities: ReadonlyArray<EntityIndexEntry>
  }) => {
    const tokens = tokensByResult(entities)
    return (
      <div className="flex flex-col gap-3" data-testid="hybrid-search-grouped">
        {entities.length > 0 && (
          <div className="bg-element flex flex-col gap-1 rounded-lg p-3" data-testid="hybrid-search-entity-legend">
            {entities.map((entity) => (
              <p
                key={entity.entityId}
                className="text-foreground-secondary break-all font-mono text-[10px]"
                title={entity.entityId}
              >
                <span className="text-foreground font-semibold">{entity.token}</span>
                {` · ${entity.resultIndices.length} result${entity.resultIndices.length === 1 ? '' : 's'} · ${entity.entityId}`}
              </p>
            ))}
          </div>
        )}
        {results.map((result, index) => (
          <div key={result.id} className="flex flex-col gap-1">
            {(tokens.get(index)?.length ?? 0) > 0 && (
              <p className="text-foreground-placeholder font-mono text-[10px]">{tokens.get(index)?.join(' ')}</p>
            )}
            <SearchResultRow result={result} />
          </div>
        ))}
      </div>
    )
  },
)

GroupedResults.displayName = 'GroupedResults'

const SearchResultRow = memo(({ result }: { result: HybridSearchResult }) => {
  // The event time the decay ranks on (edited → ended → started), mirroring the server.
  const eventTs = result.editedTs ?? result.tsEnd ?? result.ts
  const meta = [result.source, result.modality, result.unitKind, eventTs ? formatEventTime(eventTs) : null]
    .filter(Boolean)
    .join(' · ')
  const highlights = result.highlights ?? []

  return (
    <div className="flex flex-col gap-2">
      {highlights.length > 0 && (
        <div className="bg-element flex flex-col gap-1 rounded-lg p-3" data-testid="hybrid-search-highlights">
          <p className="text-foreground-placeholder text-[10px] font-medium uppercase tracking-wide">matched excerpt</p>
          {highlights.map((highlight, index) => (
            <HighlightedFragment key={`${highlight.fragmentRange[0]}-${index}`} highlight={highlight} />
          ))}
        </div>
      )}
      <Artifact
        content={result.rendering ?? '(no rendering)'}
        footer={
          <>
            <div className="min-w-0">
              <p className="text-foreground-secondary text-xs">{meta}</p>
              <p
                className="text-foreground-placeholder break-all font-mono text-[10px] leading-relaxed"
                data-testid="hybrid-search-entity-ids"
              >
                {result.entityIds.length > 0 ? result.entityIds.join('  ') : '(no entity ids)'}
              </p>
            </div>
            <p className="text-foreground-secondary shrink-0 text-xs">{`score ${result.score.toFixed(4)}`}</p>
          </>
        }
      />
    </div>
  )
})

SearchResultRow.displayName = 'SearchResultRow'

export function HighlightedFragment({
  highlight,
}: {
  highlight: NonNullable<HybridSearchResult['highlights']>[number]
}) {
  const offset = highlight.fragmentRange[0]
  const ranges = highlight.matchRanges
    .map(([start, end]) => [Math.max(0, start - offset), Math.min(highlight.text.length, end - offset)] as const)
    .filter(([start, end]) => start < end)
    .sort(([left], [right]) => left - right)
  const parts: ReactNode[] = []
  let cursor = 0
  ranges.forEach(([start, end], index) => {
    if (start > cursor) parts.push(<Fragment key={`text-${index}`}>{highlight.text.slice(cursor, start)}</Fragment>)
    if (end > cursor) {
      parts.push(
        <mark key={`match-${index}`} className="bg-yellow/30 text-foreground rounded-sm px-0.5">
          {highlight.text.slice(Math.max(cursor, start), end)}
        </mark>,
      )
    }
    cursor = Math.max(cursor, end)
  })
  if (cursor < highlight.text.length) parts.push(<Fragment key="tail">{highlight.text.slice(cursor)}</Fragment>)
  return <p className="text-foreground-secondary type-prose-xs">{parts}</p>
}
